import { RootState } from "@/reducers/store"
import { GpsConfig } from "@/reducers/gpsConfigSlice"
import { createSelector } from "@reduxjs/toolkit"

type Jid = string

const selectGpsConfigs = (state: RootState) => state.gpsConfigs

export const selectGpsConfig = (driverJid: Jid) => {
  return (state: RootState): GpsConfig | undefined =>
    state.gpsConfigs[driverJid]
}

export const getDriversWithSameDevice = (
  gpsConfigs: Record<Jid, GpsConfig>,
  driverJid: Jid,
): Record<Jid, GpsConfig> => {
  // Check if the driver exists
  if (!gpsConfigs[driverJid]) {
    return {}
  }

  const targetDeviceId = gpsConfigs[driverJid].deviceId
  const driverId = gpsConfigs[driverJid].driverId
  // If the device ID is empty, return empty array
  if (!targetDeviceId) {
    return {}
  }

  // Find all drivers with matching deviceId
  return Object.entries(gpsConfigs)
    .filter(
      ([jid, config]) =>
        config.deviceId === targetDeviceId &&
        jid !== driverJid &&
        driverId !== "" &&
        config.driverId !== "" &&
        config.driverId !== driverId,
    )
    .reduce(
      (acc, [jid, config]) => {
        acc[jid] = config
        return acc
      },
      {} as Record<Jid, GpsConfig>,
    )
}

export const createSelectDriversWithSameDevice = (
  driverJid: Jid | undefined,
) => {
  return createSelector([selectGpsConfigs], (gpsConfigs) =>
    driverJid ? getDriversWithSameDevice(gpsConfigs, driverJid) : {},
  )
}

export const isGpsConfigured = (cfg: GpsConfig | undefined): boolean => {
  if (!cfg) return false
  return Boolean(cfg.deviceId && cfg.deviceId.length > 0)
}

export const selectIsGpsConfigured = (driverJid: Jid | undefined) => {
  return createSelector([selectGpsConfigs], (gpsConfigs) => {
    if (!driverJid) return false
    return isGpsConfigured(gpsConfigs[driverJid])
  })
}
